import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableHead, Toolbar, Typography,
    CircularProgress, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, MenuItem, Chip, Autocomplete
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import PublicIcon from "@mui/icons-material/Public";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import {
    getTabbedSections, getTabbedSectionDetails, createTabbedSection, updateTabbedSection, deleteTabbedSection,
    getTabItems, getTabItemDetails, createTabItem, updateTabItem, deleteTabItem, uploadNewTabItemImage, uploadTabItemImage
} from "../services/tabbedSectionService";
import { getAllStates, getCitiesByState } from "../services/referenceService";
import { TabbedSection, TabItem } from "../types/TabSection";
import {
    StyledTableContainer,
    StyledTableCell,
    StyledTableRow,
    StyledPaper,
} from "../assets/styles/tableStyle";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";

const booleanFields = ["show"] as const;
const allFields = [
    "title",
    "subtitle",
    "imageUrl",
    "dropoffName",
    "dropoffLat",
    "dropoffLng", ...booleanFields
] as const;
const numberFields = ["dropoffLat", "dropoffLng"];
const modalFields = [
    "title",
    "subtitle",
    "dropoffName",
    "dropoffLat",
    "dropoffLng",
    ...booleanFields
] as const;

// Helper option type for Autocomplete
type CityOption = {
    label: string;
    value: string | null; // null represents GLOBAL
};

const TabbedSectionAdmin: React.FC = () => {
    // -------- Tabbed Sections (left/top table) --------
    const [sections, setSections] = useState<TabbedSection[]>([]);
    const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
    const [sectionLoading, setSectionLoading] = useState<boolean>(false);

    // Reference Data State
    const [statesList, setStatesList] = useState<any[]>([]);
    const [citiesList, setCitiesList] = useState<any[]>([]);
    const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

    // Section modal
    const [sectionModalOpen, setSectionModalOpen] = useState<boolean>(false);
    const [sectionModalMode, setSectionModalMode] = useState<"edit" | "create">("edit");
    const [selectedSection, setSelectedSection] = useState<TabbedSection | null>(null);
    const [sectionEditData, setSectionEditData] = useState<Partial<TabbedSection> & { city?: string | null }>({});
    const [sectionEditLoading, setSectionEditLoading] = useState<boolean>(false);

    // -------- Tab Items (right/bottom table) --------
    const [tabItems, setTabItems] = useState<TabItem[]>([]);
    const [tabItemLoading, setTabItemLoading] = useState<boolean>(false);

    // TabItem modal
    const [tabItemModalOpen, setTabItemModalOpen] = useState<boolean>(false);
    const [tabItemModalMode, setTabItemModalMode] = useState<"edit" | "create">("edit");
    const [selectedTabItem, setSelectedTabItem] = useState<TabItem | null>(null);
    const [tabItemEditData, setTabItemEditData] = useState<Partial<TabItem>>({});
    const [tabItemEditLoading, setTabItemEditLoading] = useState<boolean>(false);
    const [tabItemImageFile, setTabItemImageFile] = useState<File | null>(null);
    const [tabItemImagePreviewUrl, setTabItemImagePreviewUrl] = useState<string | null>(null);
    const [tabItemUploading, setTabItemUploading] = useState<boolean>(false);

    // Pagination
    const [sectionPage, setSectionPage] = useState(0);
    const [sectionRowsPerPage, setSectionRowsPerPage] = useState(10);
    const [tabItemPage, setTabItemPage] = useState(0);
    const [tabItemRowsPerPage, setTabItemRowsPerPage] = useState(10);

    // ----- Context Menus -----
    const [sectionMenu, setSectionMenu] = useState<{ top: number; left: number } | null>(null);
    const [sectionRow, setSectionRow] = useState<TabbedSection | null>(null);
    const [tabItemMenu, setTabItemMenu] = useState<{ top: number; left: number } | null>(null);
    const [tabItemRow, setTabItemRow] = useState<TabItem | null>(null);

    // ----------- Effects and fetching -----------
    useEffect(() => {
        fetchSections();
        loadStates();
    }, []);
    useEffect(() => {
        if (selectedSectionId !== null) fetchTabItems(selectedSectionId);
        else setTabItems([]);
    }, [selectedSectionId]);

    const fetchSections = async () => {
        setSectionLoading(true);
        try {
            const res = await getTabbedSections();
            setSections(res.data.items || res.data); // adjust to API response shape
            // Auto-select first section for convenience
            if ((res.data.items && res.data.items.length > 0) || (res.data.length > 0)) {
                setSelectedSectionId((res.data.items?.[0]?.id) ?? (res.data?.id));
            } else {
                setSelectedSectionId(null);
                setTabItems([]);
            }
        } finally {
            setSectionLoading(false);
        }
    };

    const loadStates = async () => {
        try {
            const res = await getAllStates();
            if (res.data.success) {
                setStatesList(res.data.states);
            }
        } catch (e) { console.error(e); }
    };

    const handleStateChange = async (stateCode: string | null) => {
        setSelectedStateCode(stateCode);
        setCitiesList([]);
        if (stateCode) {
            try {
                const res = await getCitiesByState(stateCode);
                if (res.data.success) {
                    setCitiesList(res.data.cities);
                }
            } catch (e) { console.error(e); }
        }
    };

    const fetchTabItems = async (sectionId: number) => {
        setTabItemLoading(true);
        try {
            const res = await getTabItems(sectionId);
            setTabItems(res.data.items || res.data);
        } finally {
            setTabItemLoading(false);
        }
    };

    // --------- Section CRUD ---------
    const openSectionCreateModal = () => {
        setSectionModalMode("create");
        // Initialize city as undefined so input is empty
        setSectionEditData({ city: undefined });
        setSelectedSection(null);
        setSelectedStateCode(null);
        setCitiesList([]);
        setSectionModalOpen(true);
    };
    const handleSectionRowClick = async (id: number) => {
        setSectionModalMode("edit");
        setSectionEditLoading(true);
        setSectionModalOpen(true);
        setSelectedStateCode(null);
        setCitiesList([]);
        try {
            const res = await getTabbedSectionDetails(id);
            setSelectedSection(res.data);
            setSectionEditData(res.data);
        } finally {
            setSectionEditLoading(false);
        }
    };
    const handleSectionCloseDetail = () => {
        setSectionModalOpen(false);
        setSectionEditData({});
        setSelectedSection(null);
    };
    const handleSectionFieldChange = (field: string, value: any) => {
        setSectionEditData((prev) => ({ ...prev, [field]: value }));
    };
    const handleSectionSaveEdit = async () => {
        setSectionEditLoading(true);
        try {
            // Force user to select city or global on create
            if (sectionEditData.city === undefined && sectionModalMode === 'create') {
                alert("Please select a Target Audience (Global or Specific City).");
                setSectionEditLoading(false);
                return;
            }

            const cityPayload = sectionEditData.city || null;

            if (sectionModalMode === "create") {
                const { key, title } = sectionEditData;
                await createTabbedSection({ key, title, city: cityPayload });
            } else if (sectionModalMode === "edit" && selectedSection) {
                const { key, title } = sectionEditData;
                await updateTabbedSection(selectedSection.id, { key, title, city: cityPayload });
            }
            await fetchSections();
            setSectionModalOpen(false);
        } finally {
            setSectionEditLoading(false);
        }
    };
    const handleSectionContextMenu = (event: React.MouseEvent, row: TabbedSection) => {
        event.preventDefault();
        setSectionRow(row);
        setSectionMenu({ top: event.clientY, left: event.clientX });
    };
    const handleSectionEditContext = () => {
        if (sectionRow) handleSectionRowClick(sectionRow.id);
        handleSectionCloseMenu();
    };
    const handleSectionDeleteContext = async () => {
        if (sectionRow) {
            setSectionLoading(true);
            try {
                await deleteTabbedSection(sectionRow.id);
                await fetchSections();
            } finally {
                setSectionLoading(false);
            }
        }
        handleSectionCloseMenu();
    };
    const handleSectionCloseMenu = () => setSectionMenu(null);

    // --------- TabItem CRUD ---------
    const openTabItemCreateModal = () => {
        setTabItemModalMode("create");
        setTabItemEditData({ sectionId: selectedSectionId ?? 0 });
        setSelectedTabItem(null);
        setTabItemImageFile(null);
        setTabItemImagePreviewUrl(null);
        setTabItemModalOpen(true);
    };
    const handleTabItemRowClick = async (id: number) => {
        setTabItemModalMode("edit");
        setTabItemEditLoading(true);
        setTabItemModalOpen(true);
        setTabItemImageFile(null);
        setTabItemImagePreviewUrl(null);
        try {
            const res = await getTabItemDetails(id);
            setSelectedTabItem(res.data);
            setTabItemEditData(res.data);
        } finally {
            setTabItemEditLoading(false);
        }
    };
    const handleTabItemCloseDetail = () => {
        setTabItemModalOpen(false);
        setTabItemEditData({});
        setSelectedTabItem(null);
        setTabItemImageFile(null);
        setTabItemImagePreviewUrl(null);
    };
    const handleTabItemFieldChange = (field: keyof TabItem, value: string | number | boolean) => {
        setTabItemEditData((prev) => ({ ...prev, [field]: value }));
    };
    const handleTabItemImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setTabItemImageFile(file);
            setTabItemImagePreviewUrl(URL.createObjectURL(file));
        }
    };
    const handleTabItemSaveEdit = async () => {
        setTabItemEditLoading(true);
        try {
            let imageUrl = tabItemEditData.imageUrl;
            let uploadRes: any = null;
            // Handle image upload
            if (tabItemImageFile) {
                setTabItemUploading(true);
                if (tabItemModalMode === "create") {
                    uploadRes = await uploadNewTabItemImage(tabItemImageFile);
                } else if (tabItemModalMode === "edit" && selectedTabItem) {
                    uploadRes = await uploadTabItemImage(selectedTabItem.id, tabItemImageFile);
                }
                setTabItemUploading(false);

                if (uploadRes?.data?.imageUrl) {
                    imageUrl = uploadRes.data.imageUrl;
                }
            }
            if (tabItemModalMode === "create") {
                // Requires imageUrl
                if (!imageUrl) {
                    alert("Image is required for a TabItem.");
                    return;
                }
                const { title, subtitle, show, dropoffName, dropoffLat, dropoffLng } = tabItemEditData;
                await createTabItem(selectedSectionId ?? 0, { title, subtitle, show, dropoffName, dropoffLat, dropoffLng, imageUrl });
            } else if (tabItemModalMode === "edit" && selectedTabItem) {
                const { title, subtitle, show, dropoffName, dropoffLat, dropoffLng } = tabItemEditData;
                await updateTabItem(selectedTabItem.id, { title, subtitle, show, dropoffName, dropoffLat, dropoffLng , imageUrl });
            }
            if (selectedSectionId !== null) fetchTabItems(selectedSectionId);
            setTabItemModalOpen(false);
        } finally {
            setTabItemEditLoading(false);
            setTabItemUploading(false);
            setTabItemImageFile(null);
            setTabItemImagePreviewUrl(null);
        }
    };
    const handleTabItemContextMenu = (event: React.MouseEvent, row: TabItem) => {
        event.preventDefault();
        setTabItemRow(row);
        setTabItemMenu({ top: event.clientY, left: event.clientX });
    };
    const handleTabItemEditContext = () => {
        if (tabItemRow) handleTabItemRowClick(tabItemRow.id);
        handleTabItemCloseMenu();
    };
    const handleTabItemDeleteContext = async () => {
        if (tabItemRow) {
            setTabItemLoading(true);
            try {
                await deleteTabItem(tabItemRow.id);
                if (selectedSectionId !== null) fetchTabItems(selectedSectionId);
            } finally {
                setTabItemLoading(false);
            }
        }
        handleTabItemCloseMenu();
    };
    const handleTabItemCloseMenu = () => setTabItemMenu(null);

    // ------ Derived (paginated) ------
    const paginatedSections = sections.slice(sectionPage * sectionRowsPerPage, sectionPage * sectionRowsPerPage + sectionRowsPerPage);
    const paginatedTabItems = tabItems.slice(tabItemPage * tabItemRowsPerPage, tabItemPage * tabItemRowsPerPage + tabItemRowsPerPage);

    // ------ Actions ------
    const sectionActions: ContextMenuAction[] = [
        { dividerBefore: true, label: "Edit", icon: <EditIcon color="info" fontSize="small" />, onClick: handleSectionEditContext, color: "info" },
        { label: "Delete", icon: <DeleteIcon color="error" fontSize="small" />, onClick: handleSectionDeleteContext, color: "error", dividerBefore: true }
    ];
    const tabItemActions: ContextMenuAction[] = [
        { dividerBefore: true, label: "Edit", icon: <EditIcon color="info" fontSize="small" />, onClick: handleTabItemEditContext, color: "info" },
        { label: "Delete", icon: <DeleteIcon color="error" fontSize="small" />, onClick: handleTabItemDeleteContext, color: "error", dividerBefore: true }
    ];

    // Prepare City Options
    const globalOption: CityOption = { label: "Global / All Cities", value: null };
    const cityOptions: CityOption[] = [
        globalOption,
        ...citiesList.map(c => ({ label: c.name, value: c.name }))
    ];

    let currentCityValue: CityOption | null = null;
    if (sectionEditData.city === null) {
        currentCityValue = globalOption;
    } else if (typeof sectionEditData.city === 'string') {
        const found = cityOptions.find(o => o.value === sectionEditData.city);
        currentCityValue = found || { label: sectionEditData.city, value: sectionEditData.city };
    }

    return (
        <Box sx={{ display: "flex", gap: 2, my: 2, width: "100%", maxWidth: "98vw", mx: "auto" }}>
            {/* Tabbed Sections Table */}
            <StyledPaper sx={{ flex: "1 1 30%", minWidth: 260, p: 2 }}>
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6">Tabbed Sections</Typography>
                    <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={openSectionCreateModal}>
                        Add
                    </Button>
                </Toolbar>
                {sectionLoading ? <CircularProgress sx={{ m: 2 }} /> : (
                    <StyledTableContainer>
                        <Table size="small">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>ID</StyledTableCell>
                                    <StyledTableCell>Key</StyledTableCell>
                                    <StyledTableCell>Target City</StyledTableCell>
                                    <StyledTableCell>Title</StyledTableCell>
                                    <StyledTableCell>Created At</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedSections.map((section) => (
                                    <StyledTableRow
                                        key={section.id}
                                        hover
                                        selected={selectedSectionId === section.id}
                                        onClick={() => setSelectedSectionId(section.id)}
                                        onDoubleClick={() => handleSectionRowClick(section.id)}
                                        onContextMenu={e => handleSectionContextMenu(e, section)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <StyledTableCell>{section.id}</StyledTableCell>
                                        <StyledTableCell>{section.key}</StyledTableCell>
                                        <StyledTableCell>
                                            {(section as any).city ? (
                                                <Chip
                                                    icon={<LocationCityIcon style={{color: 'white'}} />}
                                                    label={(section as any).city}
                                                    size="small"
                                                    color="primary"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<PublicIcon style={{color: 'white'}} />}
                                                    label="GLOBAL"
                                                    size="small"
                                                    color="success"
                                                />
                                            )}
                                        </StyledTableCell>
                                        <StyledTableCell>{section.title}</StyledTableCell>
                                        <StyledTableCell>{section.createdAt?.slice(0, 19) ?? "-"}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[10, 25]}
                            component="div"
                            count={sections.length}
                            rowsPerPage={sectionRowsPerPage}
                            page={sectionPage}
                            onPageChange={(_, newPage) => setSectionPage(newPage)}
                            onRowsPerPageChange={e => {
                                setSectionRowsPerPage(parseInt(e.target.value, 10));
                                setSectionPage(0);
                            }}
                        />
                    </StyledTableContainer>
                )}
                {/* Section Modal */}
                <Dialog open={sectionModalOpen} onClose={handleSectionCloseDetail} fullWidth maxWidth="sm">
                    <DialogTitle>{sectionModalMode === "edit" ? "Edit Section" : "Create Section"}</DialogTitle>
                    <DialogContent>
                        {sectionEditLoading ? <CircularProgress /> : (
                            <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                                {sectionModalMode === "edit" && selectedSection && (
                                    <TextField label="ID" value={selectedSection.id} disabled />
                                )}

                                {/* STATE SELECTOR (AUTOCOMPLETE) */}
                                <Autocomplete
                                    options={statesList}
                                    getOptionLabel={(option) => option.name}
                                    value={statesList.find(s => s.isoCode === selectedStateCode) || null}
                                    onChange={(event, newValue) => {
                                        handleStateChange(newValue ? newValue.isoCode : null);
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Filter State (To find City)" placeholder="Type to search state..." />
                                    )}
                                    fullWidth
                                />

                                {/* CITY SELECTOR (AUTOCOMPLETE) */}
                                <Autocomplete
                                    options={cityOptions}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={currentCityValue}
                                    onChange={(event, newValue) => {
                                        if (!newValue) {
                                            handleSectionFieldChange("city", undefined);
                                        } else {
                                            handleSectionFieldChange("city", newValue.value);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Target City" placeholder="Global or Search City..." />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            {option.value === null ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: 'green' }}>
                                                    <PublicIcon fontSize="small" sx={{ mr: 1 }} />
                                                    {option.label}
                                                </Box>
                                            ) : (
                                                option.label
                                            )}
                                        </li>
                                    )}
                                    fullWidth
                                    disabled={!selectedStateCode && sectionEditData.city !== null && sectionEditData.city !== undefined && !cityOptions.some(c => c.value === sectionEditData.city)}
                                />

                                <TextField
                                    label="Key"
                                    value={sectionEditData.key ?? ""}
                                    onChange={e => handleSectionFieldChange("key", e.target.value)}
                                    fullWidth
                                    required
                                    disabled={sectionModalMode === "edit"}
                                    helperText="Unique identifier (e.g. schools, pubs). Combined with City."
                                />
                                <TextField
                                    label="Title"
                                    value={sectionEditData.title ?? ""}
                                    onChange={e => handleSectionFieldChange("title", e.target.value)}
                                    fullWidth
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleSectionCloseDetail}>Close</Button>
                        <Button variant="contained" onClick={handleSectionSaveEdit} disabled={sectionEditLoading}>
                            {sectionModalMode === "edit" ? "Save" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
                <FancyContextMenu
                    actions={sectionActions}
                    open={Boolean(sectionMenu)}
                    onClose={handleSectionCloseMenu}
                    anchorPosition={sectionMenu}
                    title={sectionRow?.title || sectionRow?.key || "Row Actions"}
                />
            </StyledPaper>

            {/* Tab Items Table */}
            <StyledPaper sx={{ flex: "1 1 70%", minWidth: 440, p: 2 }}>
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6">
                        {selectedSectionId
                            ? (sections.find(s => s.id === selectedSectionId)?.title || 'Tab Items')
                            : 'Tab Items'}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={openTabItemCreateModal}
                        disabled={!selectedSectionId}
                    >
                        Add
                    </Button>
                </Toolbar>
                {tabItemLoading ? <CircularProgress sx={{ m: 2 }} /> : selectedSectionId ? (
                    <StyledTableContainer>
                        <Table>
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>ID</StyledTableCell>
                                    {allFields.map(col => <StyledTableCell key={col}>{col.charAt(0).toUpperCase() + col.slice(1)}</StyledTableCell>)}
                                    <StyledTableCell>Created At</StyledTableCell>
                                    <StyledTableCell>Updated At</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedTabItems.map(item => (
                                    <StyledTableRow
                                        key={item.id}
                                        hover
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => handleTabItemRowClick(item.id)}
                                        onContextMenu={e => handleTabItemContextMenu(e, item)}
                                    >
                                        <StyledTableCell>{item.id}</StyledTableCell>
                                        {allFields.map(field => (
                                            <StyledTableCell key={field}>
                                                {field === "imageUrl" ? (
                                                    item.imageUrl ? (
                                                        <img
                                                            src={item.imageUrl}
                                                            alt="carousel"
                                                            style={{ maxHeight: 32, borderRadius: 6 }}
                                                        />
                                                    ) : (
                                                        <ImageIcon color="disabled" />
                                                    )
                                                ) : booleanFields.includes(field as any) ? (
                                                    item[field]
                                                        ? <Chip label="Yes" color="success" size="small" />
                                                        : <Chip label="No" color="error" size="small" />
                                                ) : typeof item[field] === "number" ? (
                                                    item[field]
                                                ) : (
                                                    item[field] ?? "-"
                                                )}
                                            </StyledTableCell>
                                        ))}
                                        <StyledTableCell>
                                            {item.createdAt ? item.createdAt.slice(0, 19) : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {item.updatedAt ? item.updatedAt.slice(0, 19) : "-"}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[10,25]}
                            component="div"
                            count={tabItems.length}
                            rowsPerPage={tabItemRowsPerPage}
                            page={tabItemPage}
                            onPageChange={(_, newPage) => setTabItemPage(newPage)}
                            onRowsPerPageChange={e => {
                                setTabItemRowsPerPage(parseInt(e.target.value, 10));
                                setTabItemPage(0);
                            }}
                        />
                    </StyledTableContainer>
                ) : (
                    <Typography color="text.secondary" sx={{ my: 3 }}>Select a section to view items</Typography>
                )}
                {/* TabItem Modal */}
                <Dialog open={tabItemModalOpen} onClose={handleTabItemCloseDetail} fullWidth maxWidth="md">
                    <DialogTitle>{tabItemModalMode === "edit" ? "Edit Tab Item" : "Create Tab Item"}</DialogTitle>
                    <DialogContent>
                        {tabItemEditLoading ? <CircularProgress /> : (
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mt: 1 }}>
                                {tabItemModalMode === "edit" && selectedTabItem && (
                                    <>
                                        <TextField label="ID" value={selectedTabItem.id} fullWidth disabled />
                                        <TextField label="Created At" value={selectedTabItem.createdAt || ""} fullWidth disabled />
                                        <TextField label="Updated At" value={selectedTabItem.updatedAt || ""} fullWidth disabled />
                                    </>
                                )}
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    fullWidth
                                    disabled={tabItemUploading}
                                    sx={{ my: 1 }}
                                >
                                    {tabItemEditData.imageUrl ? "Change Image" : "Upload Image"}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleTabItemImageChange}
                                    />
                                </Button>
                                {(tabItemImagePreviewUrl || tabItemEditData.imageUrl) && (
                                    <Box sx={{ gridColumn: "span 2" }}>
                                        <img
                                            src={tabItemImagePreviewUrl || tabItemEditData.imageUrl}
                                            alt="Preview"
                                            style={{ maxWidth: "100%", maxHeight: 90, borderRadius: 10 }}
                                        />
                                    </Box>
                                )}
                                {/* Editable fields */}
                                {modalFields.filter(f => !booleanFields.includes(f as any)).map(field => (
                                    <TextField
                                        key={field}
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={tabItemEditData[field as keyof TabItem] ?? ""}
                                        fullWidth
                                        onChange={e => {
                                            const isNumber = numberFields.includes(field);
                                            handleTabItemFieldChange(
                                                field as keyof TabItem,
                                                isNumber ? Number(e.target.value) : e.target.value
                                            );
                                        }}
                                    />
                                ))}
                                {/* Boolean fields */}
                                {booleanFields.map(field => (
                                    <TextField
                                        select
                                        key={field}
                                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                                        value={
                                            tabItemEditData[field as keyof TabItem] === undefined
                                                ? "no"
                                                : tabItemEditData[field as keyof TabItem]
                                                ? "yes"
                                                : "no"
                                        }
                                        fullWidth
                                        onChange={e =>
                                            handleTabItemFieldChange(
                                                field as keyof TabItem,
                                                e.target.value === "yes"
                                            )
                                        }
                                    >
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </TextField>
                                ))}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleTabItemCloseDetail}>Close</Button>
                        <Button variant="contained" onClick={handleTabItemSaveEdit} disabled={tabItemEditLoading || tabItemUploading}>
                            {tabItemModalMode === "edit" ? "Save" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
                <FancyContextMenu
                    actions={tabItemActions}
                    open={Boolean(tabItemMenu)}
                    onClose={handleTabItemCloseMenu}
                    anchorPosition={tabItemMenu}
                    title={tabItemRow?.title || "Row Actions"}
                />
            </StyledPaper>
        </Box>
    );
};

export default TabbedSectionAdmin;
